import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { userApi } from '../api/user';
import { authApi } from '../api/auth';
import { UserResponse, ApprovalStatus, ServiceApplicationResponse, ServiceApplicationStatus } from '../types';
import { RefreshCcw, CheckCircle2, XCircle, Send, UserCheck } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const getApplicationStatusLabel = (status: ServiceApplicationStatus) => {
  if (status === 'APPROVED') return '승인됨';
  if (status === 'REJECTED') return '거절됨';
  return '대기중';
};

const getRequesterInfo = (application: ServiceApplicationResponse) => {
  const requester = application as any;
  const name =
    requester.requesterName ||
    requester.userName ||
    requester.name ||
    requester.requester?.name ||
    requester.user?.name ||
    '-';
  const email =
    requester.requesterEmail ||
    requester.userEmail ||
    requester.email ||
    requester.requester?.email ||
    requester.user?.email ||
    '-';

  return { name, email };
};

const UserRequestsPage: React.FC = () => {
  const [pendingUsers, setPendingUsers] = useState<UserResponse[]>([]);
  const [serviceApplications, setServiceApplications] = useState<ServiceApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [usersRes, applicationsRes] = await Promise.all([
        userApi.getUsers(),
        authApi.getServiceApplications(),
      ]);
      setPendingUsers((usersRes.data.result || []).filter((user) => user.approvalStatus === 'PENDING'));
      setServiceApplications((applicationsRes.data.result || []).filter((application) => application.status === 'PENDING'));
    } catch {
      setPendingUsers([]);
      setServiceApplications([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprovalUpdate = async (userId: number, approvalStatus: ApprovalStatus) => {
    try {
      await userApi.updateApproval(userId, { approvalStatus });
      await loadData();
    } catch {
      alert('승인 상태 변경 실패');
    }
  };

  const handleApplicationUpdate = async (applicationId: number, status: ServiceApplicationStatus) => {
    try {
      await authApi.updateServiceApplication(applicationId, { status });
      await loadData();
    } catch {
      alert('신청 상태 변경 실패');
    }
  };

  const stats = useMemo(
    () => ({
      pendingUsers: pendingUsers.length,
      pendingApplications: serviceApplications.length,
    }),
    [pendingUsers.length, serviceApplications.length]
  );

  if (loading) return <LoadingScreen message="유저 신청 정보를 불러오는 중" />;

  return (
    <div className="fade-in">
      <section className="card dashboard-hero-card" style={{ marginBottom: '1rem' }}>
        <div className="dashboard-hero-grid">
          <div className="dashboard-hero-copy">
            <div className="dashboard-page-kicker">유저 신청 관리</div>
            <h1 className="dashboard-hero-title" style={{ fontSize: '1.45rem', fontWeight: 700 }}>
              계정 승인과 서비스 신청을 관리합니다.
            </h1>
            <p className="dashboard-hero-description">
              승인 대기 계정과 서비스 접근 신청을 같은 화면에서 확인할 수 있습니다.
            </p>
          </div>

          <div className="dashboard-hero-stats" style={{ gridTemplateColumns: 'repeat(2, max-content)', justifyContent: 'start' }}>
            <div className="dashboard-stat-chip">
              <span>계정 승인 대기</span>
              <strong>{stats.pendingUsers}</strong>
            </div>
            <div className="dashboard-stat-chip">
              <span>서비스 신청 대기</span>
              <strong>{stats.pendingApplications}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="card dashboard-main-card dashboard-request-merge">
        <div className="dashboard-request-merge-grid">
          <div className="dashboard-request-merge-section">
          <div className="dashboard-card-head">
            <div className="dashboard-card-kicker">
              <UserCheck size={14} />
              <span>계정 승인</span>
            </div>
            <h2 className="dashboard-card-title dashboard-card-title-sm">승인 대기 계정</h2>
          </div>

          <div className="dashboard-admin-toolbar">
            <button className="btn btn-outline" type="button" onClick={loadData} disabled={refreshing}>
              <RefreshCcw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="dashboard-application-list">
            {pendingUsers.length > 0 ? (
              pendingUsers.map((user) => (
                <div key={user.id} className="dashboard-access-item">
                  <div className="dashboard-access-head">
                    <strong>{user.name}</strong>
                    <span className="badge badge-inactive">{user.approvalStatus}</span>
                  </div>
                  <div className="dashboard-access-meta">
                    <span>{user.email}</span>
                    <span>{formatDate(user.createdAt)}</span>
                  </div>
                  <div className="dashboard-admin-approval">
                    <button className="btn btn-primary" onClick={() => handleApprovalUpdate(user.id, 'APPROVED')}>
                      <CheckCircle2 size={16} /> 승인
                    </button>
                    <button className="btn btn-outline" onClick={() => handleApprovalUpdate(user.id, 'REJECTED')}>
                      <XCircle size={16} /> 거절
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="dashboard-empty-note">대기 중인 계정이 없습니다.</div>
            )}
          </div>
          </div>

          <div className="dashboard-request-merge-section">
          <div className="dashboard-card-head">
            <div className="dashboard-card-kicker">
              <Send size={14} />
              <span>서비스 신청</span>
            </div>
            <h2 className="dashboard-card-title dashboard-card-title-sm">서비스 신청 대기</h2>
          </div>

          <div className="dashboard-admin-toolbar">
            <button className="btn btn-outline" type="button" onClick={loadData} disabled={refreshing}>
              <RefreshCcw size={18} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="dashboard-application-list">
            {serviceApplications.length > 0 ? (
              serviceApplications.map((application) => (
                <div key={application.id} className="dashboard-application-item">
                  <div className="dashboard-application-top">
                    <strong>{application.serviceName || application.serviceCode}</strong>
                    <span className="badge badge-inactive">{getApplicationStatusLabel(application.status)}</span>
                  </div>
                  <div className="dashboard-application-meta">
                    <span>{getRequesterInfo(application).name}</span>
                    <span>{formatDate(application.createdAt)}</span>
                  </div>
                  <div className="dashboard-application-submeta">
                    <span>{getRequesterInfo(application).email}</span>
                  </div>
                  <div className="dashboard-admin-approval">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleApplicationUpdate(application.id, 'APPROVED')}
                    >
                      <CheckCircle2 size={16} /> 승인
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => handleApplicationUpdate(application.id, 'REJECTED')}
                    >
                      <XCircle size={16} /> 거절
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="dashboard-empty-note">대기 중인 서비스 신청이 없습니다.</div>
            )}
          </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserRequestsPage;
